import { Fragment, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BarChart } from '@mui/x-charts/BarChart';
import { Badge } from 'react-bootstrap';
import Sidebar from '../../components/sidebar/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Money, Username, useMask } from '../../utils/privacyMask';
import { getPackageColor, getPackageName, getPackagePrice, getPackageProfit, getPackageTitle } from '../../utils/packages';
import { computeRenewalScores } from '../../utils/renewalScore';
import './AnalysisPage.css';

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtMoney(n) { return `€${(parseFloat(n) || 0).toFixed(2)}`; }

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

const CHART_SX = {
  '& .MuiChartsAxis-line':      { stroke: 'var(--color-border)' },
  '& .MuiChartsAxis-tick':      { stroke: 'var(--color-border)' },
  '& .MuiChartsAxis-tickLabel': { fill: 'var(--color-muted)' },
  '& .MuiChartsLegend-label':   { fill: '#94a3b8 !important' },
  background: 'transparent',
};

export default function AnalysisPage() {
  const { mask } = useMask();
  const [subscriptions, setSubscriptions] = useState([]);
  const [accounts, setAccounts]           = useState([]);
  const [requests, setRequests]           = useState([]);
  const [issues, setIssues]               = useState([]);
  const [creditTx, setCreditTx]           = useState([]);
  const [accountsMap, setAccountsMap]     = useState({});
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [activeOnly, setActiveOnly]       = useState(false);
  const [viewMode, setViewMode]           = useState('calendar'); // 'calendar' | 'rolling'
  const [selectedYear, setSelectedYear]   = useState(new Date().getFullYear());
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [subRes, accRes, reqRes, issueRes, txRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/subscription?all=true`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/botdata/requests`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_API_URL}/botdata/issues`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_API_URL}/transaction`, { headers }).catch(() => ({ data: [] })),
        ]);
        setSubscriptions(subRes.data);
        setAccounts(accRes.data || []);
        setRequests(reqRes.data || []);
        setIssues(issueRes.data || []);
        setCreditTx((txRes.data || []).filter(t => t.type === 'reseller-credits'));
        const map = {};
        accRes.data.forEach(a => { map[a._id] = a.username; });
        setAccountsMap(map);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredSubs = useMemo(() =>
    activeOnly ? subscriptions.filter(s => s.active) : subscriptions,
    [subscriptions, activeOnly]
  );

  const now       = new Date();
  const thisKey   = monthKey(now);
  const nextKey   = monthKey(new Date(now.getFullYear(), now.getMonth() + 1, 1));

  // Every subscription's expiry month is bucketed here (unbounded — used as a lookup,
  // not rendered directly; the fixed Calendar-Year/Rolling-12 grids below pull from it).
  // A subscription that's already lapsed (active:false) still fully counts toward a
  // *past* month's predicted figure (that's what was predicted for the actual outcome
  // sitting next to it), but is excluded from a *future* month's predicted figure —
  // a cancelled sub isn't going to generate that revenue just because expireDate says so.
  const monthlyData = useMemo(() => {
    const map = {};
    filteredSubs.forEach(sub => {
      const d   = new Date(Number(sub.expireDate));
      const key = monthKey(d);
      if (!map[key]) {
        map[key] = {
          key,
          label:      `${d.toLocaleString('default', { month: 'long' })} ${d.getFullYear()}`,
          shortLabel: `${SHORT_MONTHS[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`,
          year:  d.getFullYear(),
          month: d.getMonth(),
          predictedRevenue: 0,
          predictedProfit:  0,
          actualRevenue:    0,
          actualProfit:     0,
          count: 0,
          subs:  [],
        };
      }
      const m = map[key];
      const isFuture = key > thisKey;
      if (!isFuture || sub.active !== false) {
        m.predictedRevenue += getPackagePrice(sub.package)  || 0;
        m.predictedProfit  += getPackageProfit(sub.package) || 0;
      }
      m.actualRevenue    += parseFloat(sub.paidPrice)       || 0;
      m.actualProfit     += parseFloat(sub.profitReceived)  || 0;
      m.count++;
      m.subs.push(sub);
    });
    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSubs]);

  const monthMap = useMemo(() => {
    const m = {};
    monthlyData.forEach(d => { m[d.key] = d; });
    return m;
  }, [monthlyData]);

  const emptyBucket = (key) => {
    const [y, mo] = key.split('-').map(Number);
    const d = new Date(y, mo - 1, 1);
    return {
      key,
      label:      `${d.toLocaleString('default', { month: 'long' })} ${y}`,
      shortLabel: `${SHORT_MONTHS[mo - 1]} '${String(y).slice(-2)}`,
      year: y,
      month: mo - 1,
      predictedRevenue: 0, predictedProfit: 0, actualRevenue: 0, actualProfit: 0,
      count: 0, subs: [],
    };
  };

  // Years with data, plus the current year so "Calendar Year" always has something to pick.
  const years = useMemo(() => {
    const s = new Set([...monthlyData.map(m => m.year), now.getFullYear()]);
    return Array.from(s).sort((a, b) => a - b);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyData]);

  // Fixed, always-12-month, zero-filled grid — either Jan-Dec of selectedYear, or the
  // rolling 12 months ending this month. Replaces the old "whatever months exist in the
  // data" bucketing so an empty month still shows up as €0, not as a missing row.
  const viewMonthKeys = useMemo(() => {
    if (viewMode === 'rolling') {
      return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
        return monthKey(d);
      });
    }
    return Array.from({ length: 12 }, (_, i) => `${selectedYear}-${String(i + 1).padStart(2, '0')}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedYear]);

  const filtered = useMemo(
    () => viewMonthKeys.map(key => monthMap[key] || emptyBucket(key)),
    [viewMonthKeys, monthMap]
  );

  const thisMonth = monthMap[thisKey];
  const nextMonth = monthMap[nextKey];

  const total12 = useMemo(() => {
    const cutoff = monthKey(new Date(now.getFullYear() - 1, now.getMonth(), 1));
    return monthlyData
      .filter(m => m.key >= cutoff)
      .reduce((acc, m) => ({
        predictedRevenue: acc.predictedRevenue + m.predictedRevenue,
        predictedProfit:  acc.predictedProfit  + m.predictedProfit,
        actualRevenue:    acc.actualRevenue    + m.actualRevenue,
        actualProfit:     acc.actualProfit     + m.actualProfit,
        count:            acc.count            + m.count,
      }), { predictedRevenue: 0, predictedProfit: 0, actualRevenue: 0, actualProfit: 0, count: 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyData]);

  const selectedMonthData = selectedMonthKey ? monthMap[selectedMonthKey] : null;

  // Accounts with a Renewal Score (shared formula, see utils/renewalScore.js) over 20/100 —
  // their all-time actual revenue/profit, as a "loyal customer" cohort metric.
  const renewalCohort = useMemo(() => {
    const scores = computeRenewalScores(accounts, subscriptions, requests, issues);
    const highIds = new Set(scores.filter(s => s.value > 20).map(s => s.id));
    let revenue = 0, profit = 0;
    subscriptions.forEach(s => {
      if (highIds.has(s.accountID)) {
        revenue += parseFloat(s.paidPrice)      || 0;
        profit  += parseFloat(s.profitReceived) || 0;
      }
    });
    return { accountCount: highIds.size, revenue, profit };
  }, [accounts, subscriptions, requests, issues]);

  // Credit purchases are keyed on when the reseller paid, not on a subscription
  // expiry, so they get their own buckets rather than joining monthlyData above -
  // mixing the two would conflate "revenue predicted for an expiry month" with
  // "cash taken in a month".
  const creditMonths = useMemo(() => {
    const map = {};
    creditTx.forEach(t => {
      const ts = Number(t.transactionDate);
      if (!Number.isFinite(ts)) return;
      const key = monthKey(new Date(ts));
      if (!map[key]) map[key] = { revenue: 0, profit: 0, credits: 0, count: 0 };
      map[key].revenue += parseFloat(t.revenue) || 0;
      map[key].profit  += parseFloat(t.profit)  || 0;
      map[key].credits += t.resellerCredits?.credits || 0;
      map[key].count   += 1;
    });
    return map;
  }, [creditTx]);

  // Reuses viewMonthKeys so the Calendar Year / Rolling 12 toggle drives this
  // panel too. The grid is zero-filled, so a month with no purchases reads as 0
  // rather than vanishing - which is the point when the question is how often
  // resellers actually buy.
  const creditFiltered = useMemo(
    () => filtered.map(m => ({
      key: m.key,
      label: m.label,
      shortLabel: m.shortLabel,
      ...(creditMonths[m.key] || { revenue: 0, profit: 0, credits: 0, count: 0 }),
    })),
    [filtered, creditMonths]
  );

  const creditTotals = useMemo(() => creditFiltered.reduce((acc, m) => ({
    revenue: acc.revenue + m.revenue,
    profit:  acc.profit  + m.profit,
    credits: acc.credits + m.credits,
    count:   acc.count   + m.count,
  }), { revenue: 0, profit: 0, credits: 0, count: 0 }), [creditFiltered]);

  const creditChartRevenue = creditFiltered.map(m => parseFloat((m.revenue || 0).toFixed(2)));
  const creditChartProfit  = creditFiltered.map(m => parseFloat((m.profit  || 0).toFixed(2)));
  const monthsWithPurchases = creditFiltered.filter(m => m.count > 0).length;
  const marginPct = (profit, revenue) => (revenue > 0 ? (profit / revenue) * 100 : null);

  const chartLabels     = filtered.map(m => m.shortLabel);
  const chartPredRev    = filtered.map(m => parseFloat((parseFloat(m.predictedRevenue)  || 0).toFixed(2)));
  const chartPredProfit = filtered.map(m => parseFloat((parseFloat(m.predictedProfit)   || 0).toFixed(2)));
  const chartActRev     = filtered.map(m => parseFloat((parseFloat(m.actualRevenue)     || 0).toFixed(2)));
  const chartActProfit  = filtered.map(m => parseFloat((parseFloat(m.actualProfit)      || 0).toFixed(2)));

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Financial Analysis" />
      <div className="analysis-page">

        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        {/* ── Filter bar ── */}
        <div className="analysis-filter-bar">
          <button className={`year-tab ${viewMode === 'calendar' ? 'active' : ''}`} onClick={() => setViewMode('calendar')}>Calendar Year</button>
          <button className={`year-tab ${viewMode === 'rolling' ? 'active' : ''}`} onClick={() => setViewMode('rolling')}>Rolling 12 Months</button>
          {viewMode === 'calendar' && (
            <>
              <span style={{ width: 1, height: 20, background: 'var(--color-border)', margin: '0 4px', flexShrink: 0 }} />
              {years.map(y => (
                <button key={y} className={`year-tab ${selectedYear === y ? 'active' : ''}`} onClick={() => setSelectedYear(y)}>{y}</button>
              ))}
            </>
          )}
          <label className="toggle-pill">
            <input type="checkbox" checked={activeOnly} onChange={e => { setActiveOnly(e.target.checked); setSelectedMonthKey(null); }} />
            Active only
          </label>
        </div>

        {/* ── Stat cards ── */}
        <div className="analysis-stats">
          <div className="analysis-stat" style={{ '--stat-accent': '#3b82f6' }}>
            <div className="analysis-stat-label">This Month — Pred. Revenue</div>
            <div className="analysis-stat-value">{mask(thisMonth ? fmtMoney(thisMonth.predictedRevenue) : '€0.00', 'money')}</div>
            <div className="analysis-stat-sub">{thisMonth?.count || 0} subscriptions expiring</div>
          </div>
          <div className="analysis-stat" style={{ '--stat-accent': '#06b6d4' }}>
            <div className="analysis-stat-label">This Month — Pred. Profit</div>
            <div className="analysis-stat-value">{mask(thisMonth ? fmtMoney(thisMonth.predictedProfit) : '€0.00', 'money')}</div>
            <div className="analysis-stat-sub">Actual paid: {mask(thisMonth ? fmtMoney(thisMonth.actualRevenue) : '€0.00', 'money')}</div>
          </div>
          <div className="analysis-stat" style={{ '--stat-accent': '#10b981' }}>
            <div className="analysis-stat-label">Next Month — Pred. Revenue</div>
            <div className="analysis-stat-value">{mask(nextMonth ? fmtMoney(nextMonth.predictedRevenue) : '€0.00', 'money')}</div>
            <div className="analysis-stat-sub">{nextMonth?.count || 0} subscriptions expiring</div>
          </div>
          <div className="analysis-stat" style={{ '--stat-accent': '#a78bfa' }}>
            <div className="analysis-stat-label">Rolling 12M — Pred. Revenue</div>
            <div className="analysis-stat-value">{mask(fmtMoney(total12.predictedRevenue), 'money')}</div>
            <div className="analysis-stat-sub">{total12.count} subs · Actual: {mask(fmtMoney(total12.actualRevenue), 'money')}</div>
          </div>
          <div className="analysis-stat" style={{ '--stat-accent': '#a78bfa' }}>
            <div className="analysis-stat-label">Period — Reseller Revenue</div>
            <div className="analysis-stat-value">{mask(fmtMoney(creditTotals.revenue), 'money')}</div>
            <div className="analysis-stat-sub">
              {creditTotals.count} purchase{creditTotals.count === 1 ? '' : 's'} · profit {mask(fmtMoney(creditTotals.profit), 'money')}
            </div>
          </div>
          <div className="analysis-stat" style={{ '--stat-accent': '#f472b6' }}>
            <div className="analysis-stat-label">Renewal Score &gt; 20% — Revenue</div>
            <div className="analysis-stat-value">{mask(fmtMoney(renewalCohort.revenue), 'money')}</div>
            <div className="analysis-stat-sub">{renewalCohort.accountCount} accounts · profit {mask(fmtMoney(renewalCohort.profit), 'money')}</div>
          </div>
        </div>

        {/* ── Bar chart ── */}
        <div className="analysis-panel">
          <div className="analysis-panel-header">
            <h6 className="analysis-panel-title">
              <i className="bi bi-bar-chart-fill" style={{ color: '#3b82f6' }} />
              Monthly Revenue Forecast
            </h6>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>grouped by subscription expiry month</span>
          </div>
          <div className="analysis-chart-body">
            {filtered.length > 0 ? (
              <BarChart
                xAxis={[{
                  data: chartLabels,
                  scaleType: 'band',
                  tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                  disableLine: true,
                  disableTicks: true,
                }]}
                yAxis={[{
                  tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                  disableLine: true,
                  disableTicks: true,
                }]}
                series={[
                  { data: chartPredRev,    label: 'Pred. Revenue', color: '#3b82f6', valueFormatter: v => mask(`€${v}`, 'money') },
                  { data: chartPredProfit, label: 'Pred. Profit',  color: '#06b6d4', valueFormatter: v => mask(`€${v}`, 'money') },
                  { data: chartActRev,     label: 'Actual Paid',   color: '#10b981', valueFormatter: v => mask(`€${v}`, 'money') },
                  { data: chartActProfit,  label: 'Actual Profit', color: '#a78bfa', valueFormatter: v => mask(`€${v}`, 'money') },
                ]}
                height={320}
                margin={{ left: 70, right: 20, top: 40, bottom: 60 }}
                sx={CHART_SX}
                slotProps={{
                  legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'right' },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    labelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                  },
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-muted)' }}>
                <i className="bi bi-bar-chart" style={{ fontSize: '2rem', display: 'block', marginBottom: 8 }} />
                No data for selected filters
              </div>
            )}
          </div>
        </div>

        {/* ── Monthly breakdown table ── */}
        <div className="analysis-panel">
          <div className="analysis-panel-header">
            <h6 className="analysis-panel-title">
              <i className="bi bi-table" style={{ color: '#06b6d4' }} />
              Monthly Breakdown
            </h6>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>click a row to expand subscriptions</span>
          </div>

          <div className="analysis-table-scroll">
          <table className="analysis-month-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Subs</th>
                <th>Pred. Revenue</th>
                <th>Pred. Profit</th>
                <th>Actual Paid</th>
                <th>Actual Profit</th>
                <th>Revenue Variance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: 'var(--color-muted)' }}>No data for selected filters</td>
                </tr>
              ) : (
                filtered.map(m => {
                  const variance    = m.actualRevenue - m.predictedRevenue;
                  const isSelected  = selectedMonthKey === m.key;
                  const isThisMonth = m.key === thisKey;
                  const isNextMonth = m.key === nextKey;

                  return (
                    <Fragment key={m.key}>
                      <tr
                        className={isSelected ? 'row-selected' : ''}
                        onClick={() => setSelectedMonthKey(isSelected ? null : m.key)}
                      >
                        <td style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                          {m.label}
                          {isThisMonth && <Badge bg="" style={{ background: '#3b82f6', marginLeft: 8, fontSize: '0.6rem' }}>This Month</Badge>}
                          {isNextMonth && <Badge bg="" style={{ background: '#06b6d4', marginLeft: 8, fontSize: '0.6rem' }}>Next Month</Badge>}
                        </td>
                        <td>{m.count}</td>
                        <td style={{ color: '#60a5fa' }}><Money value={m.predictedRevenue} formatter={fmtMoney} /></td>
                        <td style={{ color: '#22d3ee' }}><Money value={m.predictedProfit} formatter={fmtMoney} /></td>
                        <td style={{ color: '#34d399' }}><Money value={m.actualRevenue} formatter={fmtMoney} /></td>
                        <td style={{ color: '#c4b5fd' }}><Money value={m.actualProfit} formatter={fmtMoney} /></td>
                        <td style={{ color: variance >= 0 ? '#34d399' : '#f87171', fontWeight: 600 }}>
                          {mask(`${variance >= 0 ? '+' : ''}${fmtMoney(variance)}`, 'money')}
                        </td>
                      </tr>

                      {isSelected && selectedMonthData && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <div className="analysis-drill-panel">
                              <div className="analysis-drill-header">
                                Subscriptions expiring in {selectedMonthData.label} — {selectedMonthData.subs.length} total
                              </div>
                              <div className="analysis-table-scroll">
                              <table className="analysis-drill-table">
                                <thead>
                                  <tr>
                                    <th>Account</th>
                                    <th>Username</th>
                                    <th>Package</th>
                                    <th>Pred. Revenue</th>
                                    <th>Pred. Profit</th>
                                    <th>Actual Paid</th>
                                    <th>Actual Profit</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedMonthData.subs.map(sub => (
                                    <tr key={sub._id}>
                                      <td style={{ color: 'var(--color-text)', fontWeight: 600 }}><Username value={accountsMap[sub.accountID] || '—'} /></td>
                                      <td><Username value={sub.username || '—'} /></td>
                                      <td>
                                        <Badge bg="" style={{ background: getPackageColor(sub.package), fontSize: '0.62rem' }}>
                                          {getPackageTitle(sub.package)} – {getPackageName(sub.package)}
                                        </Badge>
                                      </td>
                                      <td style={{ color: '#60a5fa' }}><Money value={getPackagePrice(sub.package)} formatter={fmtMoney} /></td>
                                      <td style={{ color: '#22d3ee' }}><Money value={getPackageProfit(sub.package)} formatter={fmtMoney} /></td>
                                      <td style={{ color: '#34d399' }}><Money value={sub.paidPrice} formatter={fmtMoney} /></td>
                                      <td style={{ color: '#c4b5fd' }}><Money value={sub.profitReceived} formatter={fmtMoney} /></td>
                                      <td>
                                        {sub.active
                                          ? <Badge bg="success" style={{ fontSize: '0.62rem' }}>Active</Badge>
                                          : <Badge bg="danger"  style={{ fontSize: '0.62rem' }}>Inactive</Badge>
                                        }
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr>
                  <td style={{ color: 'var(--color-muted)' }}>Totals ({filtered.length} months)</td>
                  <td style={{ color: 'var(--color-muted)' }}>{filtered.reduce((s, m) => s + m.count, 0)}</td>
                  <td style={{ color: '#60a5fa' }}>{mask(fmtMoney(filtered.reduce((s, m) => s + m.predictedRevenue, 0)), 'money')}</td>
                  <td style={{ color: '#22d3ee' }}>{mask(fmtMoney(filtered.reduce((s, m) => s + m.predictedProfit,  0)), 'money')}</td>
                  <td style={{ color: '#34d399' }}>{mask(fmtMoney(filtered.reduce((s, m) => s + m.actualRevenue,    0)), 'money')}</td>
                  <td style={{ color: '#c4b5fd' }}>{mask(fmtMoney(filtered.reduce((s, m) => s + m.actualProfit,     0)), 'money')}</td>
                  <td style={{ color: 'var(--color-muted)' }}>—</td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        </div>

        {/* ── Reseller credit purchases ── */}
        <div className="analysis-panel">
          <div className="analysis-panel-header">
            <h6 className="analysis-panel-title">
              <i className="bi bi-coin" style={{ color: '#a78bfa' }} />
              Reseller Credit Purchases
            </h6>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              by payment date · not affected by the Active-only filter
            </span>
          </div>

          {creditTx.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-muted)' }}>
              <i className="bi bi-coin" style={{ fontSize: '2rem', display: 'block', marginBottom: 8, opacity: 0.4 }} />
              No reseller credit purchases logged yet
            </div>
          ) : (
            <>
              <div className="analysis-chart-body">
                <BarChart
                  xAxis={[{
                    data: creditFiltered.map(m => m.shortLabel),
                    scaleType: 'band',
                    tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                    disableLine: true,
                    disableTicks: true,
                  }]}
                  yAxis={[{
                    tickLabelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                    disableLine: true,
                    disableTicks: true,
                  }]}
                  series={[
                    { data: creditChartRevenue, label: 'Credit Revenue', color: '#a78bfa', valueFormatter: v => mask(`€${v}`, 'money') },
                    { data: creditChartProfit,  label: 'Credit Profit',  color: '#22d3ee', valueFormatter: v => mask(`€${v}`, 'money') },
                  ]}
                  height={300}
                  margin={{ left: 70, right: 20, top: 40, bottom: 60 }}
                  sx={CHART_SX}
                  slotProps={{
                    legend: {
                      direction: 'row',
                      position: { vertical: 'top', horizontal: 'right' },
                      itemMarkWidth: 10,
                      itemMarkHeight: 10,
                      labelStyle: { fill: 'var(--color-muted)', fontSize: 11 },
                    },
                  }}
                />
              </div>

              <div className="analysis-table-scroll">
                <table className="analysis-month-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Purchases</th>
                      <th>Credits</th>
                      <th>Revenue</th>
                      <th>Profit</th>
                      <th>Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditFiltered.map(m => {
                      const margin = marginPct(m.profit, m.revenue);
                      const isThisMonth = m.key === thisKey;
                      return (
                        <tr key={m.key}>
                          <td style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                            {m.label}
                            {isThisMonth && <Badge bg="" style={{ background: '#3b82f6', marginLeft: 8, fontSize: '0.6rem' }}>This Month</Badge>}
                          </td>
                          <td style={{ color: m.count ? 'var(--color-text)' : 'var(--color-subtle)' }}>{m.count}</td>
                          <td style={{ color: m.credits ? '#fbbf24' : 'var(--color-subtle)' }}>
                            {m.credits ? mask(m.credits.toLocaleString(), 'number') : '—'}
                          </td>
                          <td style={{ color: '#c4b5fd' }}><Money value={m.revenue} formatter={fmtMoney} /></td>
                          <td style={{ color: '#22d3ee' }}><Money value={m.profit}  formatter={fmtMoney} /></td>
                          <td style={{ color: margin === null ? 'var(--color-subtle)' : margin >= 0 ? '#34d399' : '#f87171', fontWeight: 600 }}>
                            {margin === null ? '—' : mask(`${margin.toFixed(1)}%`, 'number')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ color: 'var(--color-muted)' }}>
                        Totals ({creditFiltered.length} months · {monthsWithPurchases} with purchases)
                      </td>
                      <td style={{ color: 'var(--color-muted)' }}>{creditTotals.count}</td>
                      <td style={{ color: '#fbbf24' }}>{mask(creditTotals.credits.toLocaleString(), 'number')}</td>
                      <td style={{ color: '#c4b5fd' }}>{mask(fmtMoney(creditTotals.revenue), 'money')}</td>
                      <td style={{ color: '#22d3ee' }}>{mask(fmtMoney(creditTotals.profit),  'money')}</td>
                      <td style={{ color: 'var(--color-muted)' }}>
                        {marginPct(creditTotals.profit, creditTotals.revenue) === null
                          ? '—'
                          : mask(`${marginPct(creditTotals.profit, creditTotals.revenue).toFixed(1)}%`, 'number')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>

      </div>
    </>
  );
}
